import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    Settings as SettingsIcon,
    Globe,
    Database,
    Key,
    Shield,
    Wifi,
    WifiOff,
    AlertTriangle,
    RefreshCw
} from 'lucide-react';
import { usePopulatePermissions } from '@/features/admin/hooks';
import { AdminRoute } from '@/components/auth/AdminRoute';

export function SettingsPage() {
    const [languageConversionEnabled, setLanguageConversionEnabled] = useState(false);
    const [awsAccessKey, setAwsAccessKey] = useState('');
    const [awsSecretKey, setAwsSecretKey] = useState('');
    const [awsRegion, setAwsRegion] = useState('us-east-1');
    const [internetAccess, setInternetAccess] = useState(false);
    const populatePermissions = usePopulatePermissions();

    const handleSaveSettings = () => {
        // TODO: Implement save functionality
        console.log('Saving system settings:', {
            languageConversionEnabled,
            awsAccessKey,
            awsSecretKey,
            awsRegion,
            internetAccess
        });
    };

    return (
        <AdminRoute>
            <div className="space-y-6">
                {/* Header */}
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">System Settings</h1>
                    <p className="text-muted-foreground">
                        Configure system-wide settings and integrations. Admin access required.
                    </p>
                </div>

            {/* Language & Translation Settings */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Globe className="h-5 w-5" />
                        Language & Translation
                    </CardTitle>
                    <CardDescription>
                        Configure automatic language translation using AWS Translate.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                            <Label htmlFor="language-conversion">Language Conversion</Label>
                            <p className="text-sm text-muted-foreground">
                                Enable automatic translation of user interfaces and content using AWS Translate.
                            </p>
                        </div>
                        <Switch
                            id="language-conversion"
                            checked={languageConversionEnabled}
                            onCheckedChange={setLanguageConversionEnabled}
                        />
                    </div>

                    {languageConversionEnabled && (
                        <div className="space-y-4 p-4 bg-muted rounded-lg">
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label htmlFor="internet-access">Internet Access</Label>
                                    <p className="text-sm text-muted-foreground">
                                        AWS Translate requires internet connectivity.
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    {internetAccess ? (
                                        <Wifi className="h-4 w-4 text-green-500" />
                                    ) : (
                                        <WifiOff className="h-4 w-4 text-red-500" />
                                    )}
                                    <Switch
                                        id="internet-access"
                                        checked={internetAccess}
                                        onCheckedChange={setInternetAccess}
                                    />
                                </div>
                            </div>

                            {!internetAccess && (
                                <Alert>
                                    <AlertTriangle className="h-4 w-4" />
                                    <AlertDescription>
                                        Internet access is required for AWS Translate to function. Please ensure the system has internet connectivity.
                                    </AlertDescription>
                                </Alert>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="aws-access-key">AWS Access Key ID</Label>
                                    <Input
                                        id="aws-access-key"
                                        type="password"
                                        placeholder="Enter AWS Access Key ID"
                                        value={awsAccessKey}
                                        onChange={(e) => setAwsAccessKey(e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="aws-secret-key">AWS Secret Access Key</Label>
                                    <Input
                                        id="aws-secret-key"
                                        type="password"
                                        placeholder="Enter AWS Secret Access Key"
                                        value={awsSecretKey}
                                        onChange={(e) => setAwsSecretKey(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="aws-region">AWS Region</Label>
                                <Input
                                    id="aws-region"
                                    placeholder="us-east-1"
                                    value={awsRegion}
                                    onChange={(e) => setAwsRegion(e.target.value)}
                                />
                                <p className="text-sm text-muted-foreground">
                                    AWS region where Translate service is available (e.g., us-east-1, eu-west-1).
                                </p>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Security & Access Settings */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Shield className="h-5 w-5" />
                        Security & Access
                    </CardTitle>
                    <CardDescription>
                        Configure system security and access controls.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                            <Label htmlFor="vault-integration">Vault Integration</Label>
                            <p className="text-sm text-muted-foreground">
                                Manage HashiCorp Vault connections and secrets.
                            </p>
                        </div>
                        <Button variant="outline" size="sm">
                            Manage
                        </Button>
                    </div>

                    <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                            <Label htmlFor="ssl-certificates">SSL Certificates</Label>
                            <p className="text-sm text-muted-foreground">
                                Manage SSL certificates for secure communications.
                            </p>
                        </div>
                        <Button variant="outline" size="sm">
                            Manage
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Database & Storage Settings */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Database className="h-5 w-5" />
                        Database & Storage
                    </CardTitle>
                    <CardDescription>
                        Configure database and storage settings.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                            <Label htmlFor="backup-retention">Backup Retention</Label>
                            <p className="text-sm text-muted-foreground">
                                Configure how long to keep backup files and data.
                            </p>
                        </div>
                        <Button variant="outline" size="sm">
                            Configure
                        </Button>
                    </div>

                    <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                            <Label htmlFor="storage-quotas">Storage Quotas</Label>
                            <p className="text-sm text-muted-foreground">
                                Set storage limits and quotas for users and system.
                            </p>
                        </div>
                        <Button variant="outline" size="sm">
                            Configure
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* System Administration */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Shield className="h-5 w-5" />
                        System Administration
                    </CardTitle>
                    <CardDescription>
                        Administrative tools for system maintenance.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                            <Label htmlFor="populate-permissions">Populate Permissions</Label>
                            <p className="text-sm text-muted-foreground">
                                Create and assign all required permissions for all screens to roles. This will ensure all permissions exist and are properly assigned to the admin role.
                            </p>
                        </div>
                        <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => populatePermissions.mutate()}
                            disabled={populatePermissions.isPending}
                        >
                            {populatePermissions.isPending ? (
                                <>
                                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                    Populating...
                                </>
                            ) : (
                                <>
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    Populate Permissions
                                </>
                            )}
                        </Button>
                    </div>
                </CardContent>
            </Card>

                {/* Actions */}
                <div className="flex justify-end gap-2">
                    <Button variant="outline">
                        Reset to Defaults
                    </Button>
                    <Button onClick={handleSaveSettings}>
                        Save Settings
                    </Button>
                </div>
            </div>
        </AdminRoute>
    );
}
